import { useEffect, useRef, useState, type FormEvent } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, Loader2, Pencil, Play, Plus, Square, X } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { GenieMist } from "@/pages/agent-interfaces/live-agent/components/GenieMist";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * Internal demo — "Smart Capture" on the live-agent page.
 *
 * A throwaway, fully hard-coded mockup (mirrors /internal/spotlight-demo) so we
 * can FEEL two capture moments before wiring any production code:
 *   1. Pre-call opt-in line — a calm, optional "email me a recap" line under the
 *      start CTA that never blocks "Start Conversation".
 *   2. Post-call summary reveal — the instant the call ends, a glimpse of what
 *      was covered blooms out of genie mist, then a one-tap "send the full recap
 *      to my inbox". Value shown first, ask second.
 *
 * Nothing here touches LiveAgentPage, the voice flow, leads, or email. It's
 * scenery + interactions only. On brand per guidelines/design.md (gradient, brand PNG
 * icons, brand palette only — no center dialogs).
 */

const BRAND = "#4E9CFF";
const SECONDARY = "#FF4DB8";
const GRADIENT = `linear-gradient(90deg, ${BRAND} 0%, ${SECONDARY} 100%)`;

const AGENT_TAGLINE = "Your quiet meeting note-taker.";

/** What the Genie "heard" — the glimpse shown on screen (NOT the full recap). */
const HIGHLIGHTS = [
  "Walked through the Q3 roadmap priorities together",
  "Agreed to ship the billing revamp before the mobile work",
  "Next step: circulate a recap to the team by Friday",
];

/** A situational detail the Genie picked up and pre-fills for confirmation. */
const CONTEXTUAL_DETAIL = "Q3 Planning meeting";

type DemoState = "idle" | "live" | "ended";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary transition-shadow";

/* ─────────────────────────────────────────────────────────────────────────
   Shared bits
   ───────────────────────────────────────────────────────────────────────── */

/** The idle orb — mirrors VoiceAgentAvatar (soft brand-tint circle + GenieAvatar). */
const StageOrb = ({ live }: { live: boolean }) => (
  <div
    style={{
      width: "min(300px, 64vw)",
      height: "min(300px, 64vw)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `${BRAND}20`,
      boxShadow: live ? `0 0 30px ${BRAND}40` : `0 0 15px ${BRAND}20`,
      transition: "box-shadow 300ms",
    }}
  >
    <GenieAvatar
      color={BRAND}
      secondaryColor={SECONDARY}
      gradientEnabled
      size={236}
      speaking={live}
      listening={live}
    />
  </div>
);

/** A small round action affordance (Language / Type instead) — visual fidelity only. */
const MiniAction = ({ genieIcon, label }: { genieIcon: GenieIconName; label: string }) => (
  <div className="inline-flex flex-col items-center">
    <span
      className="flex h-9 w-9 items-center justify-center rounded-full bg-white"
      style={{ border: `1px solid ${BRAND}33`, color: BRAND, boxShadow: "0 4px 10px rgba(0,0,0,0.06)" }}
    >
      <GenieIcon name={genieIcon} size={18} />
    </span>
    <span className="mt-1 text-[11px] font-medium text-[#7C8B9C]">{label}</span>
  </div>
);

/** Brand-tinted soft toggle row for the consent opt-in. */
const ConsentToggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex items-center gap-2.5 text-left"
    aria-pressed={on}
  >
    <span
      className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-all"
      style={{
        background: on ? GRADIENT : "white",
        border: on ? "none" : "1.5px solid #CBD5E1",
      }}
    >
      {on && <Check size={13} strokeWidth={3} className="text-white" />}
    </span>
    <span className="text-[13px] leading-snug text-[#64748B]">
      It’s OK to follow up with a few helpful tips
    </span>
  </button>
);

/* ─────────────────────────────────────────────────────────────────────────
   1. Pre-call opt-in line — calm, optional, never blocks Start
   ───────────────────────────────────────────────────────────────────────── */

const PreCallOptIn = ({
  captured,
  onCapture,
}: {
  captured: string | null;
  onCapture: (email: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  if (captured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-2"
        style={{ border: `1px solid ${BRAND}22`, boxShadow: "0 4px 14px -8px rgba(15,23,42,0.25)" }}
      >
        <span
          className="flex h-5 w-5 items-center justify-center rounded-full text-white"
          style={{ background: GRADIENT }}
        >
          <Check size={12} strokeWidth={3} />
        </span>
        <span className="text-[13px] text-[#475569]">
          We’ll email your recap to <span className="font-semibold text-[#222B45]">{captured}</span>
        </span>
      </motion.div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <AnimatePresence initial={false} mode="wait">
        {!open ? (
          <motion.button
            key="line"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="group inline-flex items-center gap-2 text-[13px] font-medium text-[#7C8B9C] transition-colors hover:text-[#475569]"
          >
            <GenieIcon name="mail" size={15} className="h-[15px] w-[15px] opacity-70" />
            Email me a recap afterwards
            <ChevronDown size={13} className="transition-transform group-hover:translate-y-0.5" />
          </motion.button>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={(e) => {
              e.preventDefault();
              if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) onCapture(email.trim());
            }}
            className="flex items-center gap-2 overflow-hidden"
          >
            <input
              autoFocus
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-[230px] rounded-full border border-gray-300 bg-white px-4 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary"
            />
            <button
              type="submit"
              className="rounded-full px-4 py-2.5 text-[13px] font-semibold text-white shadow-md transition-transform hover:scale-[1.03]"
              style={{ background: GRADIENT }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cancel"
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              <X size={15} />
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      {open && (
        <p className="mt-2 text-[11px] text-[#9AA7B6]">We’ll send it the moment you’re done — no need to ask later.</p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   2. Post-call summary reveal — glimpse blooms, then the one-tap ask
   ───────────────────────────────────────────────────────────────────────── */

const SkeletonLine = ({ w }: { w: string }) => (
  <div className="h-3.5 rounded-full bg-gray-100 animate-pulse" style={{ width: w }} />
);

const PostCallReveal = ({
  preCaptured,
}: {
  preCaptured: string | null;
}) => {
  // The on-screen glimpse "condenses" for a beat, then the bullets settle in —
  // an interesting wait, not a spinner. The email ask is live the whole time, so
  // nobody is ever blocked on summarising.
  const [condensing, setCondensing] = useState(true);
  const [email, setEmail] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [consent, setConsent] = useState(true);
  const [detail, setDetail] = useState(CONTEXTUAL_DETAIL);
  const [editingDetail, setEditingDetail] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(preCaptured ? true : false);

  useEffect(() => {
    const t = setTimeout(() => setCondensing(false), 1500);
    return () => clearTimeout(t);
  }, []);

  const stagger = (i: number) => ({
    initial: { opacity: 0, y: 10, filter: "blur(5px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    transition: { duration: 0.4, ease: "easeOut" as const, delay: i * 0.12 },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return;
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
    }, 1100);
  };

  return (
    <div className="relative w-[min(440px,calc(100vw-2rem))]">
      <GenieMist brandColor={BRAND} secondaryColor={SECONDARY} />

      <motion.div
        initial={{ opacity: 0, y: 18, filter: "blur(18px)", scale: 0.97 }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)", scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative p-[2px] rounded-3xl"
        style={{ background: GRADIENT, boxShadow: "0 28px 70px -22px rgba(78,156,255,0.55)" }}
      >
        <div className="rounded-[22px] bg-white p-6">
          {sent ? (
            <div className="flex flex-col items-center px-2 py-6 text-center">
              <div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-full text-white"
                style={{ background: GRADIENT, boxShadow: `0 14px 32px -10px ${BRAND}90` }}
              >
                <Check size={26} strokeWidth={2.75} />
              </div>
              <h3 className="text-[18px] font-semibold text-[#222B45]">On its way</h3>
              <p className="mt-1.5 max-w-[300px] text-[14px] leading-relaxed text-[#64748B]">
                {preCaptured ? (
                  <>
                    We already had your email — the full recap is landing in{" "}
                    <span className="font-medium text-[#222B45]">{preCaptured}</span> shortly.
                  </>
                ) : (
                  <>Your full recap is being prepared and will arrive in your inbox shortly.</>
                )}
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl"
                  style={{ background: `${BRAND}14` }}
                >
                  <GenieIcon name="chat-check" size={24} className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-[17px] font-semibold leading-tight text-[#222B45]">
                    Here’s a glimpse of what we covered
                  </h3>
                  <p className="mt-0.5 text-[13px] leading-snug text-[#64748B]">
                    The full recap is one tap from your inbox.
                  </p>
                </div>
              </div>

              {/* Glimpse — condenses, then settles */}
              <div className="rounded-2xl border border-[#E5EAF0] bg-[#F9FAFC] p-4">
                {condensing ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-[12px] font-medium text-[#64748B]">
                      <Loader2 size={13} className="animate-spin" style={{ color: BRAND }} />
                      Condensing your conversation…
                    </div>
                    <SkeletonLine w="92%" />
                    <SkeletonLine w="78%" />
                    <SkeletonLine w="85%" />
                  </div>
                ) : (
                  <ul className="space-y-2.5">
                    {HIGHLIGHTS.map((h, i) => (
                      <motion.li key={i} {...stagger(i)} className="flex items-start gap-2.5">
                        <span
                          className="mt-[3px] flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full text-white"
                          style={{ background: GRADIENT }}
                        >
                          <Check size={11} strokeWidth={3} />
                        </span>
                        <span className="text-[13.5px] leading-snug text-[#475569]">{h}</span>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Contextual detail the Genie heard — pre-filled for confirmation */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[12px] font-medium text-[#9AA7B6]">Detected</span>
                {editingDetail ? (
                  <input
                    autoFocus
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    onBlur={() => setEditingDetail(false)}
                    onKeyDown={(e) => e.key === "Enter" && setEditingDetail(false)}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-[13px] focus:outline-none focus:ring-2 focus:ring-genie-primary"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingDetail(true)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-medium transition-colors"
                    style={{ background: `${BRAND}12`, color: BRAND }}
                  >
                    {detail}
                    <Pencil size={11} />
                  </button>
                )}
              </div>

              {/* The ask */}
              <form onSubmit={submit} className="mt-4 space-y-3">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                />

                {expanded && (
                  <div className="space-y-3">
                    <input type="text" autoComplete="name" placeholder="Your name (optional)" className={inputClass} />
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="Phone for follow-up (optional)"
                      className={inputClass}
                    />
                  </div>
                )}

                <ConsentToggle on={consent} onToggle={() => setConsent((v) => !v)} />

                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-1.5 self-start text-[13px] font-medium text-genie-primary transition-colors hover:underline"
                >
                  {expanded ? <X size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                  {expanded ? "Just the email is fine" : "Add name & phone for a follow-up"}
                </button>

                <button
                  type="submit"
                  disabled={sending}
                  className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95 disabled:cursor-wait disabled:opacity-70"
                  style={{ background: GRADIENT, boxShadow: `0 12px 30px -10px ${BRAND}80` }}
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    "Send the full recap to my inbox"
                  )}
                </button>

                <p className="text-center text-[11px] leading-snug text-gray-400">
                  The glimpse above is a preview — your full, polished recap arrives by email.
                </p>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Demo controls
   ───────────────────────────────────────────────────────────────────────── */

const DemoControls = ({
  state,
  onSet,
  running,
  onToggleScript,
}: {
  state: DemoState;
  onSet: (s: DemoState) => void;
  running: boolean;
  onToggleScript: () => void;
}) => {
  const steps: { key: DemoState; label: string }[] = [
    { key: "idle", label: "Pre-call" },
    { key: "live", label: "In call" },
    { key: "ended", label: "Call ended" },
  ];
  return (
    <div className="rounded-2xl border border-[#E5EAF0] bg-white px-5 py-4 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.18)]">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-[#FF4DB8] animate-pulse" />
        <span className="text-xs font-semibold text-[#222B45]">Demo controls</span>
        <span className="hidden text-xs text-[#64748B] sm:inline">— jump between the moments</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {steps.map((s) => {
          const active = state === s.key;
          return (
            <button
              key={s.key}
              onClick={() => onSet(s.key)}
              className={cn(
                "rounded-xl px-3.5 py-2 text-xs font-semibold transition-all",
                active ? "scale-[1.02] text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200",
              )}
              style={active ? { background: GRADIENT, boxShadow: "0 6px 16px -6px rgba(78,156,255,0.5)" } : undefined}
            >
              {s.label}
            </button>
          );
        })}
        <div className="mx-1 h-7 w-px bg-[#E5EAF0]" />
        <button
          onClick={onToggleScript}
          className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold text-white shadow-md transition-all hover:scale-105"
          style={{ background: GRADIENT, boxShadow: "0 6px 16px -6px rgba(78,156,255,0.5)" }}
        >
          {running ? (
            <>
              <Square size={13} /> Stop
            </>
          ) : (
            <>
              <Play size={13} /> Play the moment
            </>
          )}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

const CaptureDemo = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<DemoState>("idle");
  // Email captured pre-call — when set, the post-call card skips the ask.
  const [preCaptured, setPreCaptured] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const timers = useRef<number[]>([]);

  const clearScript = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    setRunning(false);
  };

  const set = (s: DemoState) => {
    clearScript();
    setState(s);
  };

  const runScript = () => {
    clearScript();
    setRunning(true);
    setState("idle");
    const seq: { at: number; s: DemoState }[] = [
      { at: 900, s: "live" },
      { at: 3600, s: "ended" },
    ];
    seq.forEach(({ at, s }) => timers.current.push(window.setTimeout(() => setState(s), at)));
    timers.current.push(window.setTimeout(() => setRunning(false), 3800));
  };

  useEffect(() => () => clearScript(), []);

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-[#F9FAFC]">
      <Helmet>
        <title>Smart Capture · Internal Demo</title>
      </Helmet>

      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5EAF0] bg-white/70 px-3 py-1.5 text-xs font-medium text-[#64748B] backdrop-blur transition-colors hover:bg-white hover:text-[#222B45]"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-full border border-[#E5EAF0] bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B] shadow-sm">
          Internal demo · Smart Capture
        </div>
        <div className="w-[72px]" />
      </div>

      {/* Stage */}
      <div className="relative z-0 flex min-h-dvh flex-col items-center justify-center px-4">
        <AnimatePresence mode="wait">
          {state === "ended" ? (
            <motion.div key="ended" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <PostCallReveal preCaptured={preCaptured} />
            </motion.div>
          ) : (
            <motion.div
              key="stage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center"
            >
              <p className="mb-8 text-center text-[1.15rem] font-medium text-[#475569]">{AGENT_TAGLINE}</p>

              <StageOrb live={state === "live"} />

              <button
                className="mt-7 rounded-3xl px-7 py-2.5 text-[0.95rem] font-semibold transition-all hover:-translate-y-0.5"
                style={{
                  background: state === "live" ? BRAND : `${BRAND}10`,
                  color: state === "live" ? "#fff" : BRAND,
                  border: `2px solid ${BRAND}`,
                  letterSpacing: "0.5px",
                  boxShadow: `0 4px 12px ${BRAND}20`,
                }}
              >
                {state === "live" ? "End Conversation" : "Start Conversation"}
              </button>

              {/* Action affordances (visual fidelity) */}
              <div className="mt-6 flex items-end gap-10">
                <MiniAction genieIcon="globe" label="Language" />
                <MiniAction genieIcon="chat" label="Type instead" />
              </div>

              {/* The pre-call opt-in line — only before the call, calm + optional */}
              {state === "idle" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mt-9"
                >
                  <PreCallOptIn captured={preCaptured} onCapture={setPreCaptured} />
                </motion.div>
              )}

              {state === "live" && (
                <p className="mt-9 max-w-[320px] text-center text-[12px] leading-relaxed text-[#9AA7B6]">
                  During the call we keep the existing 20-second “get a summary” pill — untouched.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Concept caption */}
      <div className="absolute inset-x-0 bottom-[112px] z-20 flex justify-center px-4">
        <p className="max-w-[560px] text-center text-[12px] leading-relaxed text-[#9AA7B6]">
          {state === "ended"
            ? "Appears the instant the call ends. The on-screen glimpse is instant (built from the live transcript); the full recap is generated and emailed afterwards — so nobody waits."
            : "A calm, optional line under the start CTA. Opt in here and we skip the post-call ask entirely."}
        </p>
      </div>

      {/* Controls */}
      <div className="absolute inset-x-0 bottom-6 z-20 flex justify-center px-4">
        <DemoControls
          state={state}
          onSet={set}
          running={running}
          onToggleScript={running ? clearScript : runScript}
        />
      </div>
    </div>
  );
};

export default CaptureDemo;
